import { useEffect } from "react";
import Link from "next/link";
import {
  Control,
  FieldValues,
  Path,
  useController,
  useWatch,
} from "react-hook-form";
import { TriangleAlert, Plus } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { useSetAtom } from "jotai";
import { updateNodeCredentialIdsAtom } from "@/store/editor-jotai";
import { IntegrationInfo, IntegrationsInfoResponse } from "@/generated";
import { useEditorReadOnly } from "@/hooks/useEditorReadOnly";

function getAllCredentials(info: IntegrationInfo) {
  return info.providers.flatMap((p) => p.credentials);
}

interface CredentialPickerFieldProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  nodeId: string;
  integrationId: string;
  label?: string;
  isDisabled?: boolean;
  nodeType?: string;
}

export const CredentialPickerField = <T extends FieldValues>({
  name,
  control,
  nodeId,
  integrationId,
  label,
  isDisabled,
  nodeType,
}: CredentialPickerFieldProps<T>) => {
  const { spotGroup } = useSpotUser();
  const updateNodeCredentialIds = useSetAtom(updateNodeCredentialIdsAtom);
  // View-only editor: the credential dropdown is disabled and the
  // integrations:edit-gated config endpoint would 403.
  const isReadOnly = useEditorReadOnly();

  const { field } = useController({ name, control });

  // V1 writes to the `credential_ids` side-channel (`_credential_id_*` field);
  // V2 stores the value directly in node_config via `field.onChange`.
  const usesSideChannel = String(name).includes("_credential_id_");

  // Adopt a pre-conversion legacy `_credential_id_*` selection instead of
  // auto-selecting the first credential (which would change a saved connection).
  const legacyName = (
    usesSideChannel
      ? name
      : String(name).replace(/[^.]+$/, `_credential_id_${integrationId}`)
  ) as Path<T>;
  const legacyWatched = useWatch({ control, name: legacyName }) as
    | string
    | null
    | undefined;
  const legacyCredId = usesSideChannel ? undefined : legacyWatched || undefined;

  const { data: integrationsConfig } = useRemoteData(
    DATA_KEYS.INTEGRATIONS_CONFIG,
    { path: { group_id: spotGroup.id } },
    { enabled: !isReadOnly }
  );

  const info = (
    integrationsConfig as IntegrationsInfoResponse | undefined
  )?.integrations.find((i) => i.type === integrationId);

  const allCredentials = info ? getAllCredentials(info) : [];
  const credentialKey = allCredentials.map((c) => c.id).join(",");

  const selectedCredId = field.value as string | null | undefined;

  // Auto-select only when no credential was selected.
  // selectedCredId is included so the effect re-fires if the form resets the value
  // (e.g. after a trigger config round-trip that returns default: null).
  useEffect(() => {
    if (allCredentials.length === 0) return;
    if (selectedCredId) return;
    // Adopt a pre-conversion legacy selection when it is still a valid
    // credential, rather than defaulting to the first one.
    if (legacyCredId && allCredentials.some((c) => c.id === legacyCredId)) {
      field.onChange(legacyCredId);
      return;
    }
    const first = allCredentials[0].id;
    field.onChange(first);
    if (usesSideChannel) {
      updateNodeCredentialIds({ nodeId, integrationId, credentialId: first });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentialKey, selectedCredId, legacyCredId, usesSideChannel]);

  // No `return null` on an empty list: a node that needs a connection must
  // still show the picker (and its "Add new connection" affordance) even when
  // nothing is connected yet — otherwise the config looks empty and the user
  // has no way to connect from here.
  const hasNoCredentials = allCredentials.length === 0;

  const selectedCred = selectedCredId
    ? allCredentials.find((c) => c.id === selectedCredId)
    : undefined;

  const hasInvalidOnly =
    !hasNoCredentials && allCredentials.every((c) => c.status !== "active");
  const isSelectedInvalid =
    !!selectedCredId &&
    (selectedCred == null || selectedCred.status !== "active");
  const isSelectedDisabledForNode =
    selectedCred != null &&
    nodeType != null &&
    (selectedCred.disabled_nodes ?? []).includes(nodeType);

  const handleValueChange = (v: string) => {
    if (v === "__add_new__") {
      window.open(
        `/settings/administration/workspaces/${spotGroup.id}?tab=linked-accounts`,
        "_blank"
      );
      return;
    }
    field.onChange(v);
    if (usesSideChannel) {
      updateNodeCredentialIds({ nodeId, integrationId, credentialId: v });
    }
  };

  return (
    <div>
      {label && <NoxusFormLabel label={label} />}
      <Select
        value={selectedCredId || ""}
        onValueChange={handleValueChange}
        disabled={isDisabled}
      >
        <SelectTrigger
          className={`w-full text-sm ${
            hasInvalidOnly || isSelectedInvalid
              ? "border-destructive"
              : isSelectedDisabledForNode
                ? "border-warning-500"
                : ""
          }`}
        >
          <SelectValue
            placeholder={
              hasNoCredentials
                ? "No connection: add one"
                : hasInvalidOnly
                  ? allCredentials[0]?.display_name || "Invalid credential"
                  : "Select account..."
            }
          />
        </SelectTrigger>
        <SelectContent>
          {selectedCredId && !selectedCred && (
            <SelectItem value={selectedCredId} disabled>
              Unavailable connection
            </SelectItem>
          )}
          {allCredentials.map((cred) => {
            const disabledForNode =
              nodeType != null &&
              (cred.disabled_nodes ?? []).includes(nodeType);
            return (
              <SelectItem key={cred.id} value={cred.id}>
                <span className="flex items-center gap-1.5">
                  {cred.display_name || cred.id}
                  {cred.status !== "active" && (
                    <TriangleAlert
                      size={12}
                      className="text-destructive shrink-0"
                    />
                  )}
                  {disabledForNode && cred.status === "active" && (
                    <TriangleAlert
                      size={12}
                      className="text-warning-500 shrink-0"
                    />
                  )}
                </span>
              </SelectItem>
            );
          })}
          <SelectSeparator />
          <SelectItem value="__add_new__" className="text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Plus size={12} className="shrink-0" />
              Add new connection
            </span>
          </SelectItem>
        </SelectContent>
      </Select>
      {hasNoCredentials && (
        <p className="text-xs text-muted-foreground mt-1.5">
          No connection yet:{" "}
          <Link
            href={`/settings/administration/workspaces/${spotGroup.id}?tab=linked-accounts`}
            target="_blank"
            className="font-medium underline hover:no-underline"
          >
            connect an account.
          </Link>
        </p>
      )}
      {(hasInvalidOnly || isSelectedInvalid) && (
        <p className="text-xs text-destructive mt-1.5">
          Invalid connection selected:{" "}
          <Link
            href={`/settings/administration/workspaces/${spotGroup.id}?tab=linked-accounts`}
            target="_blank"
            className="font-medium underline hover:no-underline"
          >
            update in connection settings.
          </Link>
        </p>
      )}
      {isSelectedDisabledForNode && !isSelectedInvalid && (
        <p className="text-xs text-warning-600 mt-1.5">
          This node is disabled for the selected connection.{" "}
          <Link
            href={`/settings/administration/workspaces/${spotGroup.id}?tab=linked-accounts`}
            target="_blank"
            className="font-medium underline hover:no-underline"
          >
            Update in connection settings.
          </Link>
        </p>
      )}
    </div>
  );
};
